import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from 'react-router-dom';
import { BookOpen, Truck, ArrowRight, Package, CheckCircle, Phone } from 'lucide-react';

export default function MKDashboard() {
  return (
    <div className="space-y-8 animate-fade-in">
      {/* Welcome Banner */}
      <Card className="bg-gradient-to-r from-[#5B3F8A] to-[#7C5BA6] text-white border-0">
        <CardContent className="py-6">
          <h2 className="font-heading text-xl font-semibold mb-2">
            Your 2026 annual book delivery is on its way!
          </h2>
          <p className="text-white/80">Expected: 30 Sep 2026</p>
        </CardContent>
      </Card>

      {/* Order Status Card */}
      <Card className="border-2 border-[#5B3F8A]/20">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-[#5B3F8A] rounded-lg flex items-center justify-center">
              <Package className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-heading text-lg font-semibold">Annual Book Order 2026</h3>
              <p className="text-sm text-slate-500">20 books | Languages: EL, CL, ML, TL</p>
            </div>
          </div>

          <div className="bg-slate-50 rounded-lg p-4 mb-4">
            <p className="text-sm text-slate-600">
              Budget: <span className="font-semibold">$550</span> | Sourced by Civica via OpenTrolley
            </p>
          </div>

          {/* Order Progress */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-xs mt-2">Confirmed</span>
            </div>
            <div className="flex-1 h-1 bg-emerald-500 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center">
                <CheckCircle className="w-5 h-5" />
              </div>
              <span className="text-xs mt-2">Processing</span>
            </div>
            <div className="flex-1 h-1 bg-emerald-500 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center animate-pulse">
                <Truck className="w-5 h-5" />
              </div>
              <span className="text-xs mt-2 font-medium text-amber-600">In Transit</span>
            </div>
            <div className="flex-1 h-1 bg-slate-200 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                <Package className="w-5 h-5 text-slate-400" />
              </div>
              <span className="text-xs mt-2 text-slate-400">Delivered</span>
            </div>
            <div className="flex-1 h-1 bg-slate-200 mx-2" />
            <div className="flex flex-col items-center">
              <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-slate-400" />
              </div>
              <span className="text-xs mt-2 text-slate-400">Acknowledged</span>
            </div>
          </div>

          <div className="text-sm text-slate-600 mb-4">
            <strong>Tracking:</strong> OT-MK-DISPATCH-BND-2026 | <strong>ETA:</strong> 30 Sep 2026
          </div>

          <div className="flex gap-2">
            <Button asChild variant="outline">
              <Link to="/mk/delivery-tracking">
                <Truck className="w-4 h-4 mr-2" />
                Track Delivery
              </Link>
            </Button>
            <Button asChild className="bg-[#5B3F8A] hover:bg-[#4A3278]">
              <Link to="/mk/book-order">
                <BookOpen className="w-4 h-4 mr-2" />
                View My Books
              </Link>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Previous Order */}
      <Card className="bg-slate-50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-slate-700">2025 Annual Order — 20 books</h3>
              <p className="text-sm text-slate-500">Delivered Oct 2025 | Acknowledged | 1 discrepancy resolved</p>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700">✓ Complete</Badge>
          </div>
        </CardContent>
      </Card>

      {/* Quick Links */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <Link to="/mk/book-order" className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-[#5B3F8A]" />
                <span className="font-medium">View My Book List</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <Link to="/mk/delivery-tracking" className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Truck className="w-5 h-5 text-[#5B3F8A]" />
                <span className="font-medium">Acknowledge Delivery</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400" />
            </Link>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow cursor-pointer">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Phone className="w-5 h-5 text-[#5B3F8A]" />
                <div>
                  <span className="font-medium">Contact Civica</span>
                  <p className="text-xs text-slate-500">6225 8800</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
